import { createStore } from 'vuex';
// REQ-LOCALE-FE-008: locale 상태 단일 진입점 모듈
import localeModule from './modules/locale';
// SPEC-CHINA-GLOBAL-RANDOMNO-BANNER-001: 전역 난수 미입력 안내 배너 상태 모듈
import missingRandomNoModule from './modules/missingRandomNo';
import chinaInapi from '@/apis/chinaInapi';
import { oneBtnDialog, jwtDecoder } from '../utils/utils';
import { bridges } from '@/utils/bridges';
import i18n from '@/i18n';


// 초기 사이드바 상태 — logout 시 reset 기준값
function initialUserInfo() {
  return {
    username: '',
    imgUrl: '',
    companyName: '',
    userId: '',
    locale: '',
  };
}

const store = createStore({
  modules: {
    // REQ-LOCALE-FE-008: locale 모듈 등록
    locale: localeModule,
    // SPEC-CHINA-GLOBAL-RANDOMNO-BANNER-001: 전역 난수 미입력 배너 모듈 등록
    missingRandomNo: missingRandomNoModule,
  },
  // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-STORE-STRICT-001 — Vuex strict mode (dev/stage 활성, prod 비활성)
  //   mutation handler 밖 state 직접 할당 시 throw. Phase 1~3 anti-pattern 정리 완료(WRITE 0건) 후
  //   향후 재도입 자동 차단 안전망. prod는 비활성(Vuex 공식 best practice, 성능 영향 회피).
  strict: process.env.NODE_ENV !== 'production',
  state: {
    // 인증 토큰 — localStorage가 진실의 원천. state는 그림자
    accessToken: '',
    refreshToken: '',
    // 로딩 인디케이터
    isLoading: false,
    // 사용자/회사 정보 (legacy userName/manager/company/companyImageUrl 필드 제거: 외부 read 0)
    userInfo: initialUserInfo(),
    // 사이드바 메뉴 권한 (categories + username)
    privileges: {},
    // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-MOBILE-SIDENAV-001 — 모바일 사이드바 토글 reactive state
    //   #new-sidenav.active 클래스 결정. 데스크탑은 CSS가 항상 표시(이 state 무관).
    //   라우트 변경 시 closeSidenav로 자동 false 갱신 (모바일 UX).
    sidenavOpen: false,
  },
  mutations: {
    setIsLoading(state, input) {
      state.isLoading = input;
    },
    // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-MOBILE-SIDENAV-001 — 모바일 사이드바 토글 mutation
    // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-MOBILE-SIDENAV-POLISH-001 Phase D — body scroll lock (D-1)
    //   body.sidenav-open 클래스 토글로 모바일에서 배경 스크롤 차단 (common.scss media query)
    //   mutation에서 DOM 조작은 Vuex 원칙상 권장 안 함이나, body는 Vue root 밖이라 reactive 불가
    //   1줄 사이드 효과 + store 단일 책임 일관성으로 mutation 유지 (향후 action 분리 후보)
    toggleSidenav(state) {
      state.sidenavOpen = !state.sidenavOpen;
      if (state.sidenavOpen) {
        document.body.classList.add('sidenav-open');
      } else {
        document.body.classList.remove('sidenav-open');
      }
    },
    // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-MOBILE-SIDENAV-001 — 라우트 변경 시 자동 close
    // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-MOBILE-SIDENAV-POLISH-001 Phase D — body scroll lock 해제 보장
    closeSidenav(state) {
      state.sidenavOpen = false;
      document.body.classList.remove('sidenav-open');
    },
    setUserInfo(state, payload) {
      // payload.locale 처리: 명시되면 localStorage 동기화, 없으면 제거
      // (App.vue setLocale와 책임 분담 — locale은 사용자 명시 선택을 우선시)
      if (payload && payload.locale) {
        state.userInfo = { ...payload };
        // @MX:NOTE: SPEC-CHINA-ADMIN-LOCALE-CORE-001 — 옛 키 'locale' 제거. admin_china_locale로 통일(store/modules/locale 단일 진입점)
      } else {
        state.userInfo = { ...initialUserInfo(), ...(payload || {}) };
      }
    },
    setPrivilegesInfo(state, payload) {
      state.privileges = payload;
    },
    // 로그아웃 시 사이드바/사용자 정보 초기화 (P2-5 fix: stale data 잔존 방지)
    // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-MOBILE-SIDENAV-POLISH-001 Phase E — sidenavOpen + body class cleanup (D-6)
    //   logout 시 모바일 사이드바 오픈 잔존 + body sidenav-open 잔존 방지 (방어적 cleanup)
    resetSidebarState(state) {
      state.userInfo = initialUserInfo();
      state.privileges = {};
      state.accessToken = '';
      state.refreshToken = '';
      state.sidenavOpen = false;
      document.body.classList.remove('sidenav-open');
    },
  },
  actions: {
    /**
     * 로그아웃 시 localStorage 정리 + store reset.
     * 호출처: Sidenav signOut.
     *
     * 이전 동작: localStorage 정리만 (state 잔존 → 다음 로그인까지 stale UI 위험)
     * 현재 동작: localStorage 정리 + commit('resetSidebarState')로 store 초기값 복원
     */
    clearLocalStorage({ commit }) {
      localStorage.removeItem('accessTokenManu');
      localStorage.removeItem('refreshTokenManu');
      localStorage.removeItem('CompanyInfo');
      localStorage.removeItem('manufacturerNameManu');
      commit('resetSidebarState');
    },
    // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-SIDEBAR-CONSOLID-001 — Vue 인스턴스 매개변수(root) 제거, dialog 호출 호출처(App.vue)에 위임
    // @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-COMPANIES-PRIVILEGES-001 B3 — /companies 호출 chinaInapi로 전환 (Authorization은 인터셉터가 부착)
    async getCompanyInfo({ commit }) {
      const companyId = jwtDecoder(localStorage.getItem('accessTokenManu'));
      if (companyId) {
        // 실패 시 throw — 호출처(App.vue)가 i18n key로 dialog 책임
        const response = await chinaInapi.get(`/companies/${companyId}`);
        if (response.status === 200 || response.status === 201) {
          const { data } = response;
          const userInfo = {
            username: data.accountInfo.name,
            imgUrl: data.imgUrl,
            companyName: data.name,
            userId: data.accountInfo.username,
            locale: data.locale,
          };
          commit('setUserInfo', userInfo);
        }
      }
    },
    /**
     * 권한 조회 단일 진입점.
     *
     * Phase 1 (SPEC-CHINA-ADMIN-STORE-HYGIENE-001):
     *   - force=true 옵션 추가. ManuAccManage/MenuModal에서 fresh fetch가 필요할 때 명시.
     *   - 변환 로직(categories.filter)을 store에 일원화 — 호출처는 변환된 결과만 받는다.
     *
     * payload 형태:
     *   - string  → username (legacy 호환)
     *   - object  → { username, force? }
     *
     * @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-COMPANIES-PRIVILEGES-001 B5 — /privileges 호출 chinaInapi로 전환 (Authorization은 인터셉터가 부착)
     * @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-STORE-HYGIENE-001 — force-refresh + 변환 일원화
     * @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-SIDEBAR-CONSOLID-001 — 실패 시 throw, dialog는 호출처 책임 (i18n key 사용)
     */
    async getPrivileges({ commit, state }, payload) {
      const companyId = jwtDecoder(localStorage.getItem('accessTokenManu'));
      if (!companyId) return null;

      // payload 정규화
      const isObject = payload && typeof payload === 'object';
      const username = isObject ? payload.username : payload;
      const force = isObject ? !!payload.force : false;

      if (!username) return null;

      // 캐시 활용: 같은 username + force=false + 이미 로드된 경우 재사용
      if (!force && state.privileges && state.privileges.username === username) {
        return state.privileges;
      }

      try {
        const response = await chinaInapi.get(`/privileges/${username}`);
        if (response.status === 200 || response.status === 201) {
          // 응답 변환: 빈 메뉴 카테 제거 (사이드바/모달 모두 동일한 변환된 결과 사용)
          if (response.data.length === 0) {
            const empty = { category: [], username: '' };
            commit('setPrivilegesInfo', empty);
            return empty;
          }
          const newArray = response.data.categories.filter((item) => item.menu.length > 0);
          const result = { category: newArray, username: response.data.username };
          commit('setPrivilegesInfo', result);
          return result;
        }
      } catch (error) {
        console.log(error);
        throw error;
      }
      return null;
    },
    /**
     * 권한 모달용 raw 응답 조회.
     *
     * getPrivileges와 분리한 이유:
     *   - 사이드바(getPrivileges)는 빈 메뉴 카테를 filter로 제거한 변환된 결과 필요
     *   - 권한 편집 모달(MenuModal)은 빈 메뉴 카테도 표시해서 권한 추가 가능해야 함
     *   - 두 호출자가 같은 endpoint를 다른 의도로 사용 → action 분리
     *
     * 캐싱 없음 — 모달 진입 시점마다 fresh fetch.
     * 에러는 호출처가 처리 (store는 throw).
     *
     * @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-STORE-HYGIENE-001 — 권한 fetch 단일화 (raw 분기)
     */
    async fetchPrivilegesRaw(_, username) {
      if (!username) throw new Error('필수 파라미터 \'username\' 누락');
      return await chinaInapi.get(`/privileges/${username}`);
    },
    /**
     * 권한 수정 (delete-and-insert) — store action 단일 진입점.
     *
     * 호출처: MenuModal updatePrivilegeInfo
     * 백엔드: china-inapi POST /privileges (SPEC-CHINA-INAPI-PRIV-CREATE-003)
     *
     * raw response 반환 — 호출처가 status code/message 사용 가능.
     * 캐시 invalidation: 자기 자신 권한을 수정한 경우 다음 force-refresh로 사이드바 갱신.
     *
     * @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-STORE-HYGIENE-001 — 권한 update fetch 단일화
     */
    async updatePrivilege(_, payload) {
      if (!payload) throw new Error('body 객체 없음');
      return await chinaInapi.post(`/privileges`, payload);
    },
    /**
     * 메뉴 마스터 카탈로그 조회 — MenuModal 피커용.
     *
     * 호출처: MenuModal getMenuList
     * 백엔드: china-inapi GET /menus/catalog (SPEC-CHINA-MENU-CATALOG-001)
     *
     * 기존 한국 inapi POST /menus/list(certilife DB 사본) 의존 제거:
     *   사이드바 hydration(GET /privileges)과 동일한 xinlaian menus + 동일 locale 값을
     *   반환하므로 MenuModal의 category/name 문자열 조인이 항상 일치한다.
     * 응답 shape { docs: [...] } — 기존 /menus/list 호환.
     *
     * @MX:NOTE: [AUTO] SPEC-CHINA-MENU-CATALOG-001 — 메뉴 마스터 단일 소스(china-inapi) 전환
     */
    // @MX:NOTE: [AUTO] 2026-07-30 — role 고정('MANUFACTURER')을 인자로 전환.
    //   고정값이면 운영자(XINLAIAN) 계정의 권한을 편집할 때도 카탈로그가 제조사 메뉴만
    //   돌려주어 GEO 메뉴가 피커에 아예 뜨지 않았다(기존 GEO 메뉴는 DB 직접 배정으로만 존재).
    //   미지정 시 기존 동작을 유지해 호출처 하위호환을 깨지 않는다.
    async fetchMenuCatalog(_ctx, role) {
      return await chinaInapi.get(`/menus/catalog`, {
        params: { role: role || 'MANUFACTURER' },
      });
    },
    /**
     * 계정별 권한 변경 이력 조회 — MenuModal 변경 이력 패널용.
     *
     * 백엔드: china-inapi GET /privileges/:username/audits (SPEC-CHINA-PRIV-AUDIT-001)
     *   - append-only 감사 이력, changedAt desc, cross-tenant 시 빈 배열.
     *   - Accept-Language는 chinaInapi 인터셉터가 부착(스냅샷은 menuId 기준이라 locale 무관).
     *
     * @MX:NOTE: [AUTO] SPEC-CHINA-PRIV-AUDIT-001 — 감사 이력 조회 store action 단일 진입점
     */
    async fetchPrivilegeAudits(_, { username, limit } = {}) {
      if (!username) throw new Error('필수 파라미터 \'username\' 누락');
      return await chinaInapi.get(`/privileges/${encodeURIComponent(username)}/audits`, { params: { limit } });
    },
    // (제거) updateCompany 액션 — 유일 호출처였던 /info 화면이 /company-profile로 대체·폐기되며
    // 함께 제거했다(2026-08-13). 자기 정보 수정은 apis/companies.js updateCompanyProfile이 담당한다.
    /**
     * 회사 비밀번호 변경 (POST /:companyId/password).
     *
     * 호출처: ForcePasswordChange 화면(최초 로그인 강제 비번 변경).
     * 백엔드: china-inapi POST /companies/:companyId/password (SPEC-CHINA-INAPI-COMPANY-MGMT-001 §4.3)
     *
     * payload: { currentPassword, newPassword } (newPassword min 8자)
     * 응답: { success: true } 또는 400/403/404 에러
     *
     * 호출처가 catch로 error.response.data.message 사용 가능.
     * 성공 시 isFirstLogin=N + passwordChangeRequired=false 자동 갱신됨 (BE 책임).
     *
     * @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-COMPANY-MGMT-001 — 비번 변경 단일 진입점
     */
    async changeCompanyPassword(_, payload) {
      if (!payload || !payload.currentPassword || !payload.newPassword) {
        throw new Error('currentPassword/newPassword가 필요합니다.');
      }
      const companyId = jwtDecoder(localStorage.getItem('accessTokenManu'));
      if (!companyId) throw new Error('로그인 정보가 없습니다.');

      return await chinaInapi.post(`/companies/${companyId}/password`, payload);
    },
    async requestAccessToken({ commit }) {
      // @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-AUTH-002 B4 — /auth/refresh chinaInapi로 전환
      //   (인터셉터 가드로 refreshToken Authorization 헤더 유지됨)
      // 세션 없음(refreshToken 부재) → 갱신 시도 생략.
      //   signin 화면에서 인증 API 401 → requestAccessToken → Bearer null /auth/refresh 401
      //   → 토큰만료 다이얼로그 + /signin 리다이렉트 → 재로드 → 401 → 무한 루프 방지.
      const refreshToken = localStorage.getItem('refreshTokenManu');
      if (!refreshToken) {
        return;
      }
      await chinaInapi.post(`/auth/refresh`, null, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
        },
      })
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            // P1-1 fix: state 직접 할당 anti-pattern 제거
            // accessToken/refreshToken은 localStorage가 진실의 원천, state 그림자 갱신은 불필요
            localStorage.setItem('accessTokenManu', response.data.accessToken);
            localStorage.setItem('refreshTokenManu', response.data.refreshToken);
            console.log('token issued');
            bridges().webToApp(response.data);
          } else {
            console.log('token not issued');
            bridges().webToApp({ success: false });
            return;
          }
        })
        .catch(async (error) => {
          console.log(error);

          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            if (window.innerWidth < 768) {
              const headers = {};
              headers['fbd-tkn'] = localStorage.getItem('accessTokenManu');
              // @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-AUTH-002 B5 — /auth/generate/mobileT chinaInapi로 전환
              const response = await chinaInapi.post(`/auth/generate/mobileT`, {}, { headers });
              if (response.data) {
                if (response.data.accessToken) {
                  localStorage.setItem('accessTokenManu', response.data.accessToken);
                }
                if (response.data.refreshToken) {
                  localStorage.setItem('refreshTokenManu', response.data.refreshToken);
                }
              }
              return;
            } else {
              bridges().webToApp({ success: false });
              commit('resetSidebarState');
              localStorage.removeItem('accessTokenManu');
              localStorage.removeItem('refreshTokenManu');
              localStorage.removeItem('CompanyInfo');
              localStorage.removeItem('manufacturerNameManu');
              // @MX:NOTE: [AUTO] SPEC-CHINA-ADMIN-SIDEBAR-CONSOLID-001 — i18n.global.t로 토큰 만료 메시지 다국어 처리
              await oneBtnDialog(i18n.global.t('token_error_title'), i18n.global.t('token_error_content'), false);
              if (error.response.status === 401) {
                window.location.href = '/signin';
              }
            }
          }
        });
    },
  },
  getters: {
    getUserInfo: state => state.userInfo,
    getPrivilegesInfo: state => state.privileges,
  },
});


// @MX:NOTE: SPEC-CHINA-ADMIN-LOCALE-M3-WATCHER — locale 변경 시 사이드바 즉시 force-refetch
// Phase 3 SPEC-CHINA-ADMIN-LOCALE-PHASE3-001 M3 단계 (deferred 후속 PR)
// store.subscribe: locale/SET_LOCALE mutation 발생 시 getPrivileges({force:true}) dispatch
//   - username은 이미 로드된 privileges에서 추출 (privileges 미로드 시 watcher skip)
//   - force:true로 캐시 우회 → 신 Accept-Language 헤더로 BE 재호출 → 사이드바 nameKo/nameZh 즉시 갱신
store.subscribe((mutation) => {
  if (mutation.type === 'locale/SET_LOCALE') {
    const username = store.state.privileges?.username;
    if (username) {
      store.dispatch('getPrivileges', { username, force: true });
    }
  }
});

export default store;
